import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { PRODUCT_CATEGORIES } from "@/data/site";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Products = () => {
  const { lang, t } = useLang();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const goto = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="products" data-testid="products-section" className="section bg-slate-50">
      <div className="container-x mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <div className="text-copper text-xs tracking-[0.3em] font-semibold uppercase">
            {t.products.eyebrow}
          </div>
          <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-navy mt-3 tracking-tight leading-[1.1]">
            {t.products.title}
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
            {t.products.body}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 md:gap-7">
          {PRODUCT_CATEGORIES.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 5) * 0.05 }}
              data-testid={`product-card-${p.key}`}
              className="group bg-white rounded-xl overflow-hidden border border-slate-200 card-hover"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={p.img}
                  alt={p.en.title}
                  loading="lazy"
                  className={`w-full h-full ${p.imgContain ? "object-fill" : "object-cover"} group-hover:scale-105 transition-transform duration-500`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 chip !text-navy !bg-white/95 shadow-sm">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-display font-semibold text-navy text-lg leading-snug">
                  {p[lang].title}
                </h3>
                <p className="text-slate-500 text-sm mt-1">{p[lang].sub}</p>
                {p[lang].desc && (
                  p[lang].link ? (
                    <a
                      href={p[lang].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-view-more-${p.key}`}
                      className="mt-3 inline-block text-sm font-semibold text-navy underline decoration-copper decoration-2 underline-offset-4 hover:text-copper transition"
                    >
                      View More
                    </a>
                  ) : (
                    <button
                      type="button"
                      data-testid={`product-view-more-${p.key}`}
                      onClick={() => setSelectedProduct(p)}
                      className="mt-3 text-sm font-semibold text-navy underline decoration-copper decoration-2 underline-offset-4 hover:text-copper transition"
                    >
                      View More
                    </button>
                  )
                )}
                <button
                  data-testid={`product-quote-${p.key}`}
                  onClick={goto}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-copper hover:text-orange-800 transition"
                >
                  {t.products.quote} <ArrowUpRight size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <Dialog open={Boolean(selectedProduct)} onOpenChange={(open) => !open && setSelectedProduct(null)}>
          {selectedProduct && (
            <DialogContent className="max-w-2xl border-slate-200 bg-white">
              <DialogHeader className="pr-8 text-left">
                <DialogTitle className="font-display text-2xl text-navy">
                  {selectedProduct[lang].title}
                </DialogTitle>
                <DialogDescription className="pt-3 text-base leading-relaxed text-slate-600">
                  {selectedProduct[lang].desc}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800">
                  Close
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          )}
        </Dialog>
      </div>
    </section>
  );
};

export default Products;
